import React from 'react';
import { Button, Card, Flex, List, Space, Tooltip, Typography } from 'antd';
import Icon, {
    DeleteOutlined,
    EditOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

import { Price } from 'features/shops/features/products/components/Product/Price';

import { ProductListProps } from '../service/products.interface';
import { useTheme } from '../../../../../hooks/useTheme';
import { selectShopCurrent } from '../../shops/store/shopSlice';
import { useManageProducts } from '../hooks/useManageProducts';
import { ProductTitle, ProductTitleType } from './Product/ProductTitle';
import { PurifiedHtml } from '../../../../../shared/ui/PurifyHtml';
import { ConfirmationDialog } from '../../../../../shared/ui/ConfirmationDialog';
import { ProductImage } from './Product/ProductImage';
import { getSrc } from '../../../../../utils/common/getSrc';
import { ArchiveIcon } from '../../../../../shared/ui/Icons/ArchiveIcon';
import { UnarchiveIcon } from '../../../../../shared/ui/Icons/UnarchiveIcon';
import { useAppSelector } from '../../../../../store';

export const ProductsCards = ({
    list,
    pagination,
    isLoading,
}: ProductListProps) => {
    const { type } = useParams();
    const { colorPrimary } = useTheme();

    const current = useAppSelector(selectShopCurrent);

    const {
        changeProductStatus,
        deleteProduct,
        isLoadingDelete,
        manageProduct,
        copyDirectLink,
        currentTooltip,
    } = useManageProducts();

    return (
        <List
            grid={{
                gutter: 16,
                xs: 1,
                sm: 1,
                md: 2,
                lg: 4,
                xl: 4,
                xxl: 4,
            }}
            loading={isLoading}
            dataSource={list?.items}
            renderItem={product => (
                <List.Item>
                    <Card
                        loading={isLoading}
                        key={product.id}
                        type='inner'
                        cover={
                            <ProductImage
                                loading={isLoading}
                                src={getSrc(
                                    `/storage/${product?.images?.[0]?.file}`
                                )}
                                gutter={1}
                                type={'web'}
                                categoryName={product.category_name}
                                style={{
                                    opacity: product.status === 3 ? '.3' : 1,
                                }}
                            />
                        }
                    >
                        <div
                            style={{
                                minHeight: 64,
                                marginBottom: '0.5rem',
                            }}
                        >
                            <ProductTitle
                                title={product.title}
                                type={ProductTitleType.page}
                            />
                        </div>

                        <StyledDescription
                            ellipsis={{
                                rows: 2,
                            }}
                        >
                            <StyledClearDescription>
                                <PurifiedHtml
                                    dirtyHtml={product?.description}
                                />
                            </StyledClearDescription>
                        </StyledDescription>

                        <Flex justify={'space-between'} align={'center'}>
                            {product.status === 3 ? (
                                <Typography.Paragraph
                                    ellipsis
                                    strong
                                    style={{
                                        color: colorPrimary,
                                        marginBottom: 0,
                                    }}
                                >
                                    Нет в наличии
                                </Typography.Paragraph>
                            ) : (
                                <Flex
                                    gap={'0 8px'}
                                    align={'baseline'}
                                    style={{ flexWrap: 'wrap' }}
                                >
                                    <Price.Current
                                        isPriceFrom={current.price_from}
                                        price={product.price}
                                    />
                                    <Price.Old price={product.old_price} />
                                </Flex>
                            )}
                            <Space>
                                <Tooltip
                                    key={product.id + 'copy'}
                                    title={currentTooltip}
                                >
                                    <StyledButton
                                        disabled={isLoading || isLoadingDelete}
                                        onClick={() =>
                                            copyDirectLink(
                                                current.id,
                                                +product.id
                                            )
                                        }
                                    >
                                        <LinkOutlined />
                                    </StyledButton>
                                </Tooltip>

                                <Tooltip
                                    key={product.id}
                                    title={
                                        product.status === (1 || 3)
                                            ? 'Добавить в архив'
                                            : 'Убрать из архива'
                                    }
                                    destroyTooltipOnHide
                                >
                                    <StyledButton
                                        onClick={() =>
                                            changeProductStatus(
                                                product.id,
                                                product.status
                                            )
                                        }
                                    >
                                        <Icon
                                            component={
                                                product.status === (1 || 3)
                                                    ? ArchiveIcon
                                                    : UnarchiveIcon
                                            }
                                        />
                                    </StyledButton>
                                </Tooltip>

                                <ConfirmationDialog
                                    activator={
                                        <StyledButton
                                            disabled={isLoadingDelete}
                                        >
                                            <DeleteOutlined />
                                        </StyledButton>
                                    }
                                    onConfirm={() => deleteProduct(product.id)}
                                    title='Удалить?'
                                    description={`Вы уверены что хотите удалить ${product.title}?`}
                                    withCheckbox
                                />

                                <StyledButton
                                    disabled={isLoading || isLoadingDelete}
                                    onClick={() =>
                                        manageProduct(type, +product.id)
                                    }
                                >
                                    <EditOutlined />
                                </StyledButton>
                            </Space>
                        </Flex>
                    </Card>
                </List.Item>
            )}
            pagination={pagination(list?.total)}
        />
    );
};

const StyledDescription = styled(Typography.Paragraph)`
    margin-bottom: 1.5rem !important;
    min-height: 44px;
`;

const StyledClearDescription = styled.span`
    & p {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }
`;

const StyledButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.5rem;
`;
